/**
 * The candidate-sweep params model. Pure: no React, no api client.
 *
 * THIS MODULE OWNS THE WIRE KEY NAMES AND NOTHING ELSE MAY RE-KEY THEM.
 *
 * The reason is `ValidationPipe({ transform: true, whitelist: true })` on the
 * backend: `whitelist: true` STRIPS unknown properties rather than rejecting
 * them. A misspelled key is therefore not a 400 — it is silently dropped, the
 * server's own default applies, and the sweep runs with settings the panel is
 * not displaying while nothing reports the discrepancy. So the keys below are
 * copied verbatim off `CandidateSearchDto`
 * (`nestjs-services/apps/admin/src/v1/dto/station-candidates.dto.ts`) and are
 * asserted against that list in the test file.
 *
 * The values mirror the RUNTIME defaults, which live in the service's `??`
 * fallbacks (`station-candidates.service.ts`), not only in the Swagger
 * `@ApiPropertyOptional({ default })` annotations — Swagger defaults are
 * documentation and `ValidationPipe` does not apply them. Both agree today;
 * the service is the authority. Mirroring them means the panel shows the
 * operator what the sweep will actually do, and the estimate is quoted from
 * the same numbers that get billed.
 *
 * Deliberately NOT here: the four bbox scalars (`swLng`, `swLat`, `neLng`,
 * `neLat`). They are required and un-defaulted server-side, come from the drawn
 * rectangle rather than the params panel, and are merged into the request body
 * separately by `StationCandidatesApi`. There is also no `cityId` — the DTO has
 * none (the corridor filter measures against lane geometry near the swept box,
 * and each candidate's city label is derived from its own coordinates), so a
 * `cityId` would be whitelisted away.
 */

/**
 * The thirteen FOOD types, verbatim from the food half of
 * `DEFAULT_INCLUDED_PRIMARY_TYPES` (`apps/admin/src/station-candidates.filters.ts`),
 * in declared order. Counter-service, seating, no host stand — the siting
 * intent is riders WAITING, not driver parking.
 *
 * Order matters: `requestCount` is cells x types, so the estimate readout and
 * the panel's type list should agree with the backend's own ordering, and a
 * reordered copy makes diffing the two by eye useless.
 */
export const DEFAULT_FOOD_PRIMARY_TYPES = [
  'cafe',
  'coffee_shop',
  'bakery',
  'sandwich_shop',
  'donut_shop',
  'bagel_shop',
  'deli',
  'fast_food_restaurant',
  'food_court',
  'cafeteria',
  'ice_cream_shop',
  'juice_shop',
  'tea_house',
];

/**
 * `LODGING_PRIMARY_TYPES` (`station-candidates.filters.ts`). A staffed 24h
 * lobby with indoor seating is the whole qualification, which is why the list
 * stops at these two: `motel`, `inn`, `hostel`, `bed_and_breakfast`,
 * `guest_house` and `campground` typically have no staffed public lobby and are
 * deliberately excluded server-side.
 *
 * DO NOT ADD A THIRD without checking the cap. Requests are cells x types, and
 * 16 types at the 64-cell cap is 1,024 against `MAX_REQUESTS` of 1,000 — a
 * third lodging type makes a full-cap sweep unrunnable rather than merely
 * expensive.
 */
export const LODGING_PRIMARY_TYPES = ['hotel', 'resort_hotel'];

/**
 * The backend's fifteen, in its order: food first, then lodging.
 *
 * WHY THIS COPY STILL EXISTS, rather than omitting `includedPrimaryTypes` from
 * the request and letting the server default apply. Omitting would have been
 * the fix if the list were only wire data — but the panel RENDERS this list as
 * the operator's type checkboxes, so a client-side copy is needed either way.
 * Omitting therefore would not delete the duplication; it would only stop the
 * copy from being sent, leaving it unasserted and free to drift silently. Which
 * is exactly how `hotel`/`resort_hotel` came to be never swept: the backend
 * moved to fifteen types, the UI kept sending its explicit thirteen, and
 * `dto.includedPrimaryTypes?.length ? dto.includedPrimaryTypes : DEFAULT` meant
 * the UI's list always won.
 *
 * So the copy stays AND is asserted against the DTO's own list in the test
 * file. That test is the guard; the sending is what makes the panel's display
 * true. If you change this list, change `station-candidates.filters.ts` in the
 * same PR, and vice versa.
 */
export const DEFAULT_INCLUDED_PRIMARY_TYPES = [
  ...DEFAULT_FOOD_PRIMARY_TYPES,
  ...LODGING_PRIMARY_TYPES,
];

/**
 * The service-layer caps, for the panel's fine print. Enforcement is
 * server-side (`estimate` reports `overCap`; `generate` throws 400 over the
 * cap), and the estimate response echoes `maxCells`/`maxRequests`, so PREFER
 * the echoed values when rendering — these are a pre-estimate hint only.
 */
export const MAX_CELLS = 64;
export const MAX_REQUESTS = 1000;

/**
 * What a full-cap sweep costs in requests, for the panel's fine print.
 *
 * 15 types x 64 cells = 960, which fits under `MAX_REQUESTS` with 40 to spare —
 * so the default sweep is runnable at the cell cap, but only just. Dropping
 * lodging takes it to 832. Quoted here rather than computed in the panel so the
 * two numbers cannot disagree about which type list they describe.
 */
export const FULL_CAP_REQUESTS = MAX_CELLS * DEFAULT_INCLUDED_PRIMARY_TYPES.length;
export const FULL_CAP_REQUESTS_WITHOUT_LODGING = MAX_CELLS * DEFAULT_FOOD_PRIMARY_TYPES.length;

/**
 * Every defaulted optional field of `CandidateSearchDto`, with the service's
 * runtime default.
 *
 * The two rating pairs (`minRating`/`minUserRatingCount` and their `lodging`
 * counterparts) are ABSENT ON PURPOSE — see `UNDEFAULTED_PARAM_KEYS`.
 */
export const DEFAULT_SEARCH_PARAMS = {
  // DEFAULT_CELL_RADIUS_METERS, station-candidates.grid.ts
  cellRadiusMeters: 2000,
  includedPrimaryTypes: DEFAULT_INCLUDED_PRIMARY_TYPES,
  // DEFAULT_FILTER_PARAMS.excludedPrimaryTypes / service `?? []`
  excludedPrimaryTypes: [],
  // service: `dto.maxResultsPerCell ?? 20`
  maxResultsPerCell: 20,
  // The five DEFAULT_FILTER_PARAMS entries, station-candidates.filters.ts.
  //
  // ALL FOUR OF THESE NOW GOVERN FOOD CANDIDATES ONLY. Lodging resolves against
  // `DEFAULT_LODGING_FILTER_PARAMS` instead — dine-in not required (Google
  // rarely populates `dineIn` for hotels and `evaluatePlace` treats unknown as
  // failing, so requiring it would drop nearly every hotel) and a 00:00-23:59
  // window. `allowMissingOpeningHours` and `excludedPrimaryTypes` stay
  // sweep-wide. The panel labels them accordingly; see `SearchParamsPanel`.
  requireDineIn: true,
  openByLocalTime: '06:00',
  openUntilLocalTime: '21:00',
  openAllSevenDays: true,
  allowMissingOpeningHours: true,
  // service-module constants DEFAULT_DEDUPE_RADIUS_METERS / _CORRIDOR_
  dedupeRadiusMeters: 150,
  corridorFilterEnabled: true,
  corridorRadiusMeters: 4828,
  // `includeLodging` default true (`CandidateSearchDto`). False removes the two
  // lodging types from the sweep and is the cost escape hatch — the ONLY param
  // here that changes the request count rather than the keep/drop decision.
  includeLodging: true,
};

/**
 * The four optional fields with NO server default.
 *
 * The service passes them straight through as possibly-`undefined`
 * (`minRating: dto.minRating`), and `evaluatePlace` gates on
 * `params.minRating !== undefined` precisely so an absent threshold means "no
 * threshold" — a null rating is UNKNOWN, not zero, and coercing it would drop
 * every newly-opened place, the kind most worth finding.
 *
 * So sending `minRating: 0` is NOT equivalent to omitting it: 0 is a real
 * filter that changes which places are kept. They must be omitted when unset,
 * which is what `toRequestParams` does.
 */
export const UNDEFAULTED_PARAM_KEYS = [
  'minRating',
  'minUserRatingCount',
  // THE LODGING PAIR IS SEPARATE ON PURPOSE, not a duplicate of the two above.
  // A 4.2 hotel and a 4.2 cafe are not the same bar, and hotels carry far more
  // reviews — so one threshold across both classes would either wave through
  // mediocre hotels or exclude every cafe worth siting. They are undefaulted for
  // exactly the same reason as the food pair: 0 is a real filter, not "no
  // filter", so unset must mean OMITTED.
  'lodgingMinRating',
  'lodgingMinUserRatingCount',
];

/**
 * `laneIds` — THE LANES THE SWEEP MEASURES AGAINST. Deliberately absent from
 * `DEFAULT_SEARCH_PARAMS`.
 *
 * It is a wire field of `CandidateSearchDto` like every other key in this
 * module, but it is not a panel field and has no default: it is DERIVED from
 * which city pairs the operator has selected on the builder page, so there is
 * no sensible value to fill in when it is absent and nothing here can invent
 * one. `toRequestParams` therefore passes it through when it is a non-empty
 * array and omits it otherwise, the same shape as `UNDEFAULTED_PARAM_KEYS`.
 *
 * WHY IT GOES THROUGH THE PARAMS OBJECT AT ALL rather than being merged in at
 * the api layer: it changes which candidates come back, so it has to be inside
 * the two things computed FROM the params object — the estimate's query key
 * (`useCandidateEstimate` serialises the whole request, so coverage is
 * structural rather than remembered) and `requestParamsKey`, the page's drift
 * guard. Merging it in below those two would mean editing the lane selection
 * after a sweep silently changed the request while the page still claimed the
 * listed candidates matched the settings on screen — which is the same defect
 * class the drift guard exists to close.
 *
 * WHY THE COPY IS SORTED. Lane order is meaningless to the backend (unlike
 * `includedPrimaryTypes`, whose order drives the request fan-out), but
 * `stableStringify` preserves array order — so an unsorted list would make the
 * estimate refetch, and the drift guard fire, whenever the lane fetch happened
 * to return the same lanes in a different order.
 *
 * NOT VALIDATED BY `validateParams`. An empty selection is only invalid when
 * `corridorFilterEnabled` is true, and the remedy is a checkbox in the page's
 * lane sidebar, not a field in the params panel — an error keyed here would
 * render as "fix the highlighted fields" beside nothing highlighted. The page
 * gates on it directly and says which control to use.
 */
export const LANE_IDS_KEY = 'laneIds';

/**
 * Per-field bounds, transcribed from `CandidateSearchDto`'s validators so the
 * panel's inputs constrain themselves from ONE source instead of each control
 * inventing its own min/max.
 *
 * `integer: true` marks `@IsInt()` (vs `minRating`'s `@IsNumber()`).
 * `maxItems` marks `@ArrayMaxSize`, `nonEmpty` marks `@ArrayNotEmpty`.
 */
export const PARAM_BOUNDS = {
  cellRadiusMeters: { min: 100, max: 50000, integer: true },
  includedPrimaryTypes: { maxItems: 50, nonEmpty: true },
  excludedPrimaryTypes: {},
  maxResultsPerCell: { min: 1, max: 20, integer: true },
  minRating: { min: 0, max: 5 },
  minUserRatingCount: { min: 0, integer: true },
  lodgingMinRating: { min: 0, max: 5 },
  lodgingMinUserRatingCount: { min: 0, integer: true },
  requireDineIn: {},
  openByLocalTime: { pattern: 'HH:MM' },
  openUntilLocalTime: { pattern: 'HH:MM' },
  openAllSevenDays: {},
  allowMissingOpeningHours: {},
  dedupeRadiusMeters: { min: 0, max: 5000, integer: true },
  corridorFilterEnabled: {},
  corridorRadiusMeters: { min: 0, max: 50000, integer: true },
  includeLodging: {},
  // No numeric bounds; listed so this object still covers every optional field
  // of the DTO. `nonEmpty` is conditional — see `LANE_IDS_KEY` — and is enforced
  // by the builder page, not by `validateParams`.
  laneIds: { nonEmpty: true },
};

/** `LOCAL_TIME` from the DTO, character for character. */
const LOCAL_TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

const isBlank = (value) => value === undefined || value === null || value === '';

const numberError = (value, { min, max, integer }) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) {
    return 'Must be a number';
  }
  if (integer && !Number.isInteger(parsed)) {
    return 'Must be a whole number';
  }
  if (min !== undefined && parsed < min) {
    return `Must be at least ${min}`;
  }
  if (max !== undefined && parsed > max) {
    return `Must be at most ${max}`;
  }
  return null;
};

const ARRAY_KEYS = ['includedPrimaryTypes', 'excludedPrimaryTypes'];

const NUMERIC_KEYS = [
  'cellRadiusMeters',
  'maxResultsPerCell',
  'minRating',
  'minUserRatingCount',
  'lodgingMinRating',
  'lodgingMinUserRatingCount',
  'dedupeRadiusMeters',
  'corridorRadiusMeters',
];

/**
 * Field errors for a params object, keyed by wire field name. `{}` means valid.
 *
 * Mirrors the DTO's validators so an operator sees the problem beside the
 * input instead of a 400 after the fact — but it is a convenience, not the
 * authority. The server still validates.
 *
 * Blank numerics are treated as "unset": for the two undefaulted keys that is
 * legal (omitted from the request), and for the rest `toRequestParams` falls
 * back to the default, so neither is an error here.
 */
export const validateParams = (params = {}) => {
  const errors = {};

  NUMERIC_KEYS.forEach((key) => {
    if (isBlank(params[key])) {
      return;
    }
    const error = numberError(params[key], PARAM_BOUNDS[key]);
    if (error) {
      errors[key] = error;
    }
  });

  const types = params.includedPrimaryTypes;
  if (types !== undefined) {
    if (!Array.isArray(types) || types.length === 0) {
      errors.includedPrimaryTypes = 'Select at least one place type';
    } else if (types.length > PARAM_BOUNDS.includedPrimaryTypes.maxItems) {
      errors.includedPrimaryTypes = `Select at most ${PARAM_BOUNDS.includedPrimaryTypes.maxItems} place types`;
    }
  }

  if (params.excludedPrimaryTypes !== undefined && !Array.isArray(params.excludedPrimaryTypes)) {
    errors.excludedPrimaryTypes = 'Must be a list of place types';
  }

  // BLANK IS UNSET HERE TOO, matching the numerics above and — more importantly —
  // matching `toRequestParams`, which treats a blank time as absent and sends the
  // server default. Erroring on blank while the request quietly succeeded with
  // '06:00' meant a cleared field blocked Generate over a value the sweep would
  // never have used.
  ['openByLocalTime', 'openUntilLocalTime'].forEach((key) => {
    if (isBlank(params[key])) {
      return;
    }
    if (typeof params[key] !== 'string' || !LOCAL_TIME.test(params[key])) {
      errors[key] = 'Must be a time as HH:MM';
    }
  });

  // Not a DTO rule — the backend accepts an inverted window and simply keeps
  // nothing, which looks like "the area has no cafes" rather than like a typo.
  //
  // Compared on the RESOLVED values, since a blank field means the server default
  // rather than "no window": a 22:00 open-by against a blank open-until is still
  // inverted once `toRequestParams` fills in 21:00, and it is exactly as invisible.
  if (!errors.openByLocalTime && !errors.openUntilLocalTime) {
    const byBlank = isBlank(params.openByLocalTime);
    const untilBlank = isBlank(params.openUntilLocalTime);
    const by = byBlank ? DEFAULT_SEARCH_PARAMS.openByLocalTime : params.openByLocalTime;
    const until = untilBlank ? DEFAULT_SEARCH_PARAMS.openUntilLocalTime : params.openUntilLocalTime;
    if (typeof by === 'string' && typeof until === 'string' && by >= until) {
      errors.openUntilLocalTime = untilBlank
        ? `Must be later than the open-by time (blank sends the default ${DEFAULT_SEARCH_PARAMS.openUntilLocalTime})`
        : 'Must be later than the open-by time';
    }
  }

  return errors;
};

/**
 * The request body's params half: defaults filled in, blanks resolved, and the
 * two undefaulted keys — plus `laneIds` — OMITTED unless actually set.
 *
 * Emitting `minRating: 0` for an empty input would silently apply a real
 * filter, so the omission is the whole point of this function. Numerics are
 * coerced because DOM inputs hand back strings and `@IsInt()` rejects those
 * (`@Type(() => Number)` runs first, but `'abc'` becomes `NaN` and 400s —
 * validate before calling this).
 */
export const toRequestParams = (params = {}) => {
  const merged = { ...DEFAULT_SEARCH_PARAMS };

  Object.keys(DEFAULT_SEARCH_PARAMS).forEach((key) => {
    if (!isBlank(params[key])) {
      merged[key] = params[key];
    }
  });

  NUMERIC_KEYS.forEach((key) => {
    if (key in merged) {
      merged[key] = Number(merged[key]);
    }
  });

  UNDEFAULTED_PARAM_KEYS.forEach((key) => {
    if (!isBlank(params[key])) {
      merged[key] = Number(params[key]);
    }
  });

  // Arrays are COPIED, never handed back by reference. Without this,
  // `toRequestParams({}).includedPrimaryTypes === DEFAULT_INCLUDED_PRIMARY_TYPES`
  // — so any caller that sorted or pushed to the result would corrupt the module's
  // defaults for every later call in the session, including the estimate's.
  ARRAY_KEYS.forEach((key) => {
    if (Array.isArray(merged[key])) {
      merged[key] = [...merged[key]];
    }
  });

  // See `LANE_IDS_KEY`: passed through only when actually selected, and sorted
  // so an order-only difference is not treated as a different request.
  if (Array.isArray(params[LANE_IDS_KEY]) && params[LANE_IDS_KEY].length > 0) {
    merged[LANE_IDS_KEY] = [...params[LANE_IDS_KEY]].sort();
  }

  return merged;
};

/**
 * `JSON.stringify` with object keys sorted at every level, so two equal values
 * always produce the same string.
 *
 * Lives here, beside the normaliser, because every consumer that needs a stable
 * identity for a candidate request needs it AFTER `toRequestParams` — the query
 * key in `useCandidateEstimate`, and `requestParamsKey` below. One serialiser
 * next to the one normaliser; a second copy is how the estimate's cache key and
 * the page's drift check would come to disagree about whether two requests are
 * the same request.
 *
 * Arrays keep their order — `includedPrimaryTypes` order is meaningful to the
 * backend's request fan-out, so sorting it would misrepresent the request.
 */
export const stableStringify = (value) =>
  JSON.stringify(value, (key, raw) => {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      return Object.keys(raw)
        .sort()
        .reduce((sorted, sortedKey) => ({ ...sorted, [sortedKey]: raw[sortedKey] }), {});
    }
    return raw;
  });

/**
 * A STABLE IDENTITY FOR "THE PARAMS A SWEEP WAS RUN WITH".
 *
 * Normalised first, then serialised, and in that order for a reason: the point
 * of this key is to answer "would a sweep run now differ from the one that was
 * billed?", so it must be computed from THE REQUEST, not from the panel's raw
 * field values. `toRequestParams` is what resolves a blank field to the server
 * default, coerces `'4.5'` to `4.5`, and omits `minRating` / `minUserRatingCount`
 * when unset — so a cosmetic edit that resolves to the same request (clearing a
 * field that held the default, retyping the same number as a string, handing the
 * same values back in a different key order) is correctly NOT drift, while
 * `minRating` going from unset to `0` correctly IS.
 *
 * Using the panel's raw state instead would raise a false alarm on every one of
 * those, and false alarms on a screen whose only remedy is "spend $8 again" get
 * learned around fast.
 */
export const requestParamsKey = (params = {}) => stableStringify(toRequestParams(params));

export default DEFAULT_SEARCH_PARAMS;
